// pages/sections/bpprReviewDemoData.js
//
// Tour-only sample data for the BPPR Review section. Shown ONLY while the
// spotlight tour runs (gated by lib/tourDemo.js) so every step always has an
// ideal example to point at, regardless of the viewer's real queue. Same
// nerd-reference cast of fictional companies as the rest of the app's demos.
// Matches the /api/diBpprReview response shape consumed by BpprReviewSection:
//   { source, adminView, employees:[{email,name}], rows:[…] }
// Each row curates one workflow state so the walkthrough can point at it:
//   assigned-not-accepted · accepted · reviewed · handed-off · handed-to-me ·
//   unassigned · closed.
//
// Archivable: delete this file + revert the BpprReviewSection demo edits.

// Fictional staff (clearly not real BMSS employees) used for the manager avatars
// and the reject picker's employee dropdown.
const STAFF = {
  scott:      { email: 'michael.scott@demo.bmss.com',     name: 'Michael Scott' },
  schrute:    { email: 'dwight.schrute@demo.bmss.com',    name: 'Dwight Schrute' },
  beesly:     { email: 'pam.beesly@demo.bmss.com',        name: 'Pam Beesly' },
  halpert:    { email: 'jim.halpert@demo.bmss.com',       name: 'Jim Halpert' },
  martin:     { email: 'angela.martin@demo.bmss.com',     name: 'Angela Martin' },
  martinez:   { email: 'oscar.martinez@demo.bmss.com',    name: 'Oscar Martinez' },
  malone:     { email: 'kevin.malone@demo.bmss.com',      name: 'Kevin Malone' },
  flax:       { email: 'holly.flax@demo.bmss.com',        name: 'Holly Flax' },
  hudson:     { email: 'stanley.hudson@demo.bmss.com',    name: 'Stanley Hudson' },
  flenderson: { email: 'toby.flenderson@demo.bmss.com',   name: 'Toby Flenderson' },
};

const DEMO_QUESTIONNAIRE = 'https://forms.office.com/r/bppr-demo-questionnaire';

// Managers block: { serviceManagers:{cas,tax,assurance}, serviceManagerNames:{…} }
// from a { cas, tax, assurance } spec of STAFF keys (null = no manager for that
// line, which renders the "N/A" avatar).
function managers({ cas, tax, assurance }) {
  const pick = (k) => (k ? STAFF[k] : null);
  const c = pick(cas), t = pick(tax), a = pick(assurance);
  return {
    serviceManagers:     { cas: c && c.email, tax: t && t.email, assurance: a && a.email },
    serviceManagerNames: { cas: c && c.name,  tax: t && t.name,  assurance: a && a.name },
  };
}

const ROWS = [
  // Assigned to you, not yet accepted → Accept / Reject / Close & Don't Roll.
  {
    jobIdx: 'D-700201', clientCode: '10010-000', clientName: 'Stark Industries, Inc.',
    jobName: '2027 BPPR Return', jobOffice: 'Birmingham',
    ...managers({ cas: 'schrute', tax: 'scott', assurance: 'beesly' }),
    priorYearHours: '128.50', priorYearWip: '$3,900.00', priorYearBilling: '$12,400.00',
    questionnaireUrl: DEMO_QUESTIONNAIRE,
    assigned: true, accepted: false, reviewed: false,
    wipStaff: [STAFF.schrute, STAFF.halpert, STAFF.martinez],
  },
  // Accepted (owned) but not yet reviewed → "Accepted ✓" + Close; Reviewed toggle enabled.
  {
    jobIdx: 'D-700202', clientCode: '10222-000', clientName: 'Rye and Sons Aviation',
    jobName: '2027 BPPR Return', jobOffice: 'Huntsville',
    ...managers({ cas: 'halpert', tax: 'martin', assurance: 'martinez' }),
    priorYearHours: '42.00', priorYearWip: '$980.00', priorYearBilling: '$3,600.00',
    questionnaireUrl: null,
    assigned: true, accepted: true, reviewed: false,
    wipStaff: [STAFF.halpert, STAFF.malone],
  },
  // Accepted + reviewed → green, signed-off row.
  {
    jobIdx: 'D-700203', clientCode: '10333-000', clientName: 'Aperture Science Corp.',
    jobName: '2027 BPPR Return', jobOffice: 'Gadsden',
    ...managers({ cas: 'malone', tax: 'flax', assurance: 'hudson' }),
    priorYearHours: '210.00', priorYearWip: '$7,500.00', priorYearBilling: '$28,000.00',
    questionnaireUrl: DEMO_QUESTIONNAIRE,
    assigned: true, accepted: true, reviewed: true, reviewedBy: 'You',
    wipStaff: [STAFF.malone, STAFF.hudson],
  },
  // Handed off by you → red "Waiting on acceptance".
  {
    jobIdx: 'D-700204', clientCode: '10444-000', clientName: 'Sweetums',
    jobName: '2027 BPPR Return', jobOffice: 'Nashville',
    ...managers({ cas: 'scott', tax: 'schrute', assurance: 'beesly' }),
    priorYearHours: '12.00', priorYearWip: '$250.00', priorYearBilling: '$1,100.00',
    questionnaireUrl: null,
    assigned: true, accepted: false, reviewed: false,
    handoffPending: true, handoffTo: STAFF.schrute.email,
    wipStaff: [STAFF.scott, STAFF.schrute],
  },
  // Handed to you → "Pending your acceptance": Accept / Decline.
  {
    jobIdx: 'D-700205', clientCode: '10555-000', clientName: 'Wayne Enterprises',
    jobName: '2027 BPPR Return', jobOffice: 'Birmingham',
    ...managers({ cas: 'martin', tax: 'halpert', assurance: 'martinez' }),
    priorYearHours: '66.00', priorYearWip: '$2,100.00', priorYearBilling: '$7,800.00',
    questionnaireUrl: DEMO_QUESTIONNAIRE,
    assigned: true, accepted: false, reviewed: false, pendingForMe: true,
    wipStaff: [STAFF.martin, STAFF.halpert],
  },
  // Unassigned → Acknowledge (no owner; note the "N/A" Tax manager avatar).
  {
    jobIdx: 'D-700206', clientCode: '10666-000', clientName: 'The Bannered Mare',
    jobName: '2027 BPPR Return', jobOffice: 'Birmingham',
    ...managers({ cas: 'hudson', tax: null, assurance: 'flenderson' }),
    priorYearHours: '24.00', priorYearWip: '$520.00', priorYearBilling: '$2,000.00',
    questionnaireUrl: null,
    assigned: false, accepted: false, reviewed: false,
    wipStaff: [STAFF.hudson],
  },
  // Closed & won't roll → Closed tag (+ admin Undo, since adminView is on below).
  {
    jobIdx: 'D-700207', clientCode: '10777-000', clientName: 'OsCorp Laboratories',
    jobName: '2027 BPPR Return', jobOffice: 'Nashville',
    ...managers({ cas: 'beesly', tax: 'scott', assurance: 'malone' }),
    priorYearHours: '88.00', priorYearWip: '$4,200.00', priorYearBilling: '$9,900.00',
    questionnaireUrl: null,
    assigned: true, accepted: true, reviewed: false, closed: true, closedBy: 'You',
    wipStaff: [STAFF.beesly, STAFF.scott],
  },
];

// A fresh deep copy each call so a tour's optimistic clicks never mutate the
// shared fixture (and the next tour run starts clean).
export function bpprDemoData() {
  return {
    source: 'demo',
    adminView: true,
    employees: Object.values(STAFF),
    rows: ROWS.map(r => ({
      ...r,
      mine: true,   // the tour walks the viewer's OWN queue (own-row action buttons)
      serviceManagers: { ...r.serviceManagers },
      serviceManagerNames: { ...r.serviceManagerNames },
      wipStaff: (r.wipStaff || []).map(s => ({ ...s })),
    })),
  };
}
